import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
	Box,
	IconButton,
	Paper,
	type PaperProps,
	Snackbar,
	Tooltip,
	Typography,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { type ReactNode, useState } from "react";
import { useGlobalState } from "../globalState";

interface PaperSectionProps extends Omit<PaperProps, "title"> {
	title?: ReactNode;
	actions?: ReactNode;
	getText?: () => string;
	children: ReactNode;
	sx?: SxProps<Theme>;
	headerSx?: SxProps<Theme>;
}

export const PaperSection = ({
	title,
	actions,
	getText,
	children,
	sx,
	headerSx,
	...rest
}: PaperSectionProps) => {
	const { currentUser } = useGlobalState();
	const isAdmin = !!(currentUser?.is_staff || currentUser?.is_superuser);
	const showCopy = !!getText && isAdmin;
	const showHeader = title !== undefined || actions !== undefined || showCopy;
	return (
		// A section is as wide as the page gives it and what it holds — a chart,
		// a table of many columns — is often wider than a phone. It scrolls
		// inside its own card rather than pushing the document sideways; 28
		// tables sit directly in one of these, and this is the whole of their
		// fix. `...sx` still wins, for a section that wants something else.
		<Paper sx={{ p: 2, overflowX: "auto", ...sx }} {...rest}>
			{showHeader && (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						mb: 2,
						...headerSx,
					}}
					data-component="paper-section-header"
				>
					{title !== undefined &&
						(typeof title === "string" ? (
							<Typography variant="h6" sx={{ flexGrow: 1, minWidth: 0 }}>
								{title}
							</Typography>
						) : (
							<Box sx={{ flexGrow: 1, minWidth: 0 }}>{title}</Box>
						))}
					{title === undefined && <Box sx={{ flexGrow: 1 }} />}
					{actions}
					{showCopy && getText && <CopyIconButton getText={getText} />}
				</Box>
			)}
			{children}
		</Paper>
	);
};

export const CopyIconButton = ({ getText }: { getText: () => string }) => {
	const [open, setOpen] = useState(false);
	return (
		<>
			<Tooltip title="Copy for LLM">
				<IconButton
					data-component="copy-section-button"
					size="small"
					onClick={(e) => {
						e.stopPropagation();
						navigator.clipboard.writeText(getText());
						setOpen(true);
					}}
				>
					<ContentCopyIcon fontSize="small" />
				</IconButton>
			</Tooltip>
			<Snackbar
				open={open}
				autoHideDuration={2000}
				onClose={() => setOpen(false)}
				message="Copied"
			/>
		</>
	);
};
